import { ReactNode } from 'react';

import {
  useField,
  FieldProps,
  FieldInputProps,
  FieldRenderProps,
} from 'react-final-form';

import { SelectInput as Input } from '@vk-hr-tek/ui/input';
import { OptionBadge } from '@vk-hr-tek/ui/input/common';

import { compose, ValidateType } from '../validation';

/**
 * Функция-обработчик изменения значения поля ввода.
 *
 * @param input - Объект с методами и свойствами для управления значением поля ввода.
 * @param onChange - Функция, вызываемая при изменении значения поля ввода.
 * @returns Функция, которая изменяет значение поля ввода и вызывает переданную функцию-обработчик.
 */
const handleChange =
  (
    input: FieldInputProps<
      string | number | (string | number)[] | undefined,
      HTMLElement
    >,
    onChange: (
      value: string | number | (string | number)[] | undefined,
    ) => void,
  ) =>
  (value: string | number | (string | number)[] | undefined) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

type SelectInputProps = {
  labelIcon?: ReactNode;
  multiple?: boolean;
  defaultValue?: string | string[] | number | number[];
  validate?: (ValidateType | undefined)[] | ValidateType;
  items: {
    label: string;
    value: string | number;
    badge?: OptionBadge;
    group?: string;
    disabled?: boolean;
  }[];
  validationWithoutTouch?: boolean;
  validationWhenDisabled?: boolean;
  withoutMargins?: boolean;
} & FieldProps<
  string | string[] | number | number[] | undefined,
  FieldRenderProps<string | string[] | number | number[] | undefined>
>;

/**
 * Компонент ввода выбора.
 *
 * @public
 * @param label - Метка для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Всплывающая подсказка для поля ввода.
 * @param name - Имя поля ввода, используемое для связывания с формой.
 * @param onChange - Функция, вызываемая при изменении значения поля ввода.
 * @param placeholder - Значение по умолчанию отображаемое в поле ввода до выбора значения.
 * @param showError - Флаг, определяющий, следует ли отображать ошибку при взаимодействии с полем.
 * @param required - Флаг, указывающий, является ли поле обязательным для заполнения.
 * @param disabled - Флаг, определяющий, доступно ли поле для взаимодействия.
 * @param autocomplete - Флаг, указывающий, поддерживает ли поле автозавершение.
 * @param clearable - Флаг, определяющий, можно ли очистить значение поля.
 * @param loading - Флаг, указывающий, находится ли поле в состоянии загрузки.
 * @param items - Массив элементов выбора.
 * @param defaultValue - Значение поля ввода по умолчанию.
 * @param renderOption - Функция для рендера опции выбора.
 * @param testId - Идентификатор для тестирования компонента.
 * @param emptyOption - Опция для отображения пустого значения.
 * @param validate - Функция или массив функций валидации.
 * @param validationWithoutTouch - Показывать ошибку без взаимодействия с полем.
 * @param validationWhenDisabled - Показывать ошибку если поле задизейблено.
 * @param withoutMargins - Флаг для отключения отступов.
 * @param multiple - Разрешён ли выбор нескольких значений.
 * @param rest - Остальные пропсы, передаваемые в {@link useField}.
 * @returns Компонент ввода выбора.
 */
const SelectInput = ({
  label,
  labelIcon,
  tooltip,
  name,
  onChange,
  placeholder,
  showError = true,
  required = false,
  disabled = false,
  autocomplete = false,
  clearable = false,
  loading = false,
  multiple = false,
  items,
  defaultValue,
  renderOption,
  testId,
  emptyOption,
  validate,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  withoutMargins = false,
  ...rest
}: SelectInputProps) => {
  const { input, meta } = useField(name, {
    initialValue: defaultValue,
    validate: Array.isArray(validate) ? compose(validate) : validate,
    ...rest,
  });

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled.
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки.
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  if (multiple) {
    return (
      <Input
        {...input}
        multiple
        label={label}
        labelIcon={labelIcon}
        onChange={handleChange(input, onChange)}
        placeholder={placeholder}
        value={input.value as (string | number)[]}
        items={items}
        required={required}
        disabled={disabled}
        autocomplete={autocomplete}
        clearable={clearable}
        loading={loading}
        error={renderError}
        helperText={renderError && showError && meta.error}
        tooltip={tooltip}
        renderOption={renderOption}
        testId={testId}
        emptyOption={emptyOption}
        withoutMargins={withoutMargins}
      />
    );
  }

  return (
    <Input
      {...input}
      label={label}
      labelIcon={labelIcon}
      onChange={handleChange(input, onChange)}
      placeholder={placeholder}
      value={input.value as string | number}
      items={items}
      required={required}
      disabled={disabled}
      autocomplete={autocomplete}
      clearable={clearable}
      loading={loading}
      multiple={multiple}
      error={renderError}
      helperText={renderError && showError && meta.error}
      tooltip={tooltip}
      renderOption={renderOption}
      testId={testId}
      emptyOption={emptyOption}
      withoutMargins={withoutMargins}
    />
  );
};

export default SelectInput;
